import React from "react";
import {
  declareAiCapability,
  useProvideAiCapability,
} from "../../ai-omnibar/ai-capability-registry";

/**
 * What the HCR state compliance list (Uploads) can do for the AI omnibar.
 *
 * Its date range is the caregiver's Medflyt hire date rather than anything about the upload, so the
 * parameters say so - "hired last year" is what this page can answer, not "uploaded last year".
 */
declareAiCapability({
  id: "hcrStateCompliance.filter",
  title: "Filter the HCR state compliance list",
  effect: "prefill",
  description:
    "Sets the filters on the Caregiver HCR State Compliance list: a free-text search over name, id or state registry number, and the caregiver's Medflyt hire date range. Only works while that page is open.",
  location: "the filter bar of the Caregiver HCR State Compliance page",
  scope: "page",
  // The state itself carries no permission, and the page is reachable by anyone who can open it, so
  // gating this on the Uploads permission would withhold a capability for a page the person can use.
  permissionKey: "view_caregivers_section_compliance",
  params: [
    {
      name: "search",
      type: "text",
      required: false,
      searchesFreeText: true,
      description: "Free text matched against caregiver name, id or state registry number.",
    },
    {
      name: "hiredFrom",
      type: "date",
      required: false,
      description: "Earliest Medflyt hire date to include, as yyyy-MM-dd.",
    },
    {
      name: "hiredTo",
      type: "date",
      required: false,
      description: "Latest Medflyt hire date to include, as yyyy-MM-dd.",
    },
  ],
  examples: ["state compliance for caregivers hired this year", "uploads for smith"],
});

export function useHcrStateComplianceAiCapabilities(params: {
  /** The page's own setFilter, typed loosely: this hook only sets keys it declares. */
  setFilter: (key: string, value: unknown) => void;
}): void {
  const { setFilter } = params;

  const setFilterRef = React.useRef(setFilter);
  setFilterRef.current = setFilter;

  useProvideAiCapability("hcrStateCompliance.filter", (args) => {
    const applied: string[] = [];

    if (args.search !== undefined) {
      setFilterRef.current("search", String(args.search));
      applied.push(`"${String(args.search)}"`);
    }

    if (args.hiredFrom !== undefined) {
      setFilterRef.current("fromHireDate", String(args.hiredFrom));
    }

    if (args.hiredTo !== undefined) {
      setFilterRef.current("toHireDate", String(args.hiredTo));
    }

    if (args.hiredFrom !== undefined || args.hiredTo !== undefined) {
      applied.push(`hired ${[args.hiredFrom, args.hiredTo].filter(Boolean).join(" to ")}`);
    }

    return applied.length === 0
      ? "Re-applied the state compliance filters"
      : `Showing state compliance: ${applied.join(", ")}`;
  });
}
